import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ArrowLeft, Save, Loader2, CheckCircle } from "lucide-react";
import { BusinessProfileForm } from "@/components/forms/BusinessProfileForm";
import { AdminStatusCard } from "@/components/admin/AdminStatusCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const adminBusinessPath = (business) => encodeURIComponent(business?.id || business?.business_email || "");

const AdminBusinessEditPage = () => {
  const { email } = useParams();
  const isNew = email === "new";
  const navigate = useNavigate();
  const token = localStorage.getItem("bizkeep_admin_token");

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [business, setBusiness] = useState({
    business_name: "", business_email: "", instagram: "", facebook: "",
    tiktok: "", business_type: "", business_categories: [], business_category_other: "", address: "", business_addresses: [], additional_business_locations: [], is_home_based: false, website: "", hours: "",
    business_description: "", location: "", business_locations: [], phone_number: "", logo: "",
  });

  const fetchBusiness = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/business/${encodeURIComponent(email)}`, { headers: { Authorization: `Bearer ${token}` } });
      setBusiness(r.data);
    } catch {
      toast.error("Failed to load business");
      navigate("/admin/dashboard");
    } finally { setLoading(false); }
  }, [email, token, navigate]);

  useEffect(() => {
    if (!token) { navigate("/admin"); return; }
    if (!isNew) fetchBusiness();
  }, [token, isNew, navigate, fetchBusiness]);

  const handleChange = (field, value) => {
    setBusiness((p) => ({ ...p, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (isNew && !business.business_email) {
      toast.error("Business email is required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/admin/business`, business, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Business created");
        navigate("/admin/dashboard");
      } else {
        const response = await axios.put(`${API}/admin/business/${encodeURIComponent(email)}`, { ...business, profile_review_needed: false }, { headers: { Authorization: `Bearer ${token}` } });
        setBusiness(response.data);
        toast.success("Business updated");
        setHasChanges(false);
        if (response.data?.id && response.data.id !== email) {
          navigate(`/admin/business/${adminBusinessPath(response.data)}`, { replace: true });
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save business");
    } finally { setSaving(false); }
  };

  // Admin actions
  const refresh = useCallback(async () => {
    if (!isNew) {
      const r = await axios.get(`${API}/admin/business/${encodeURIComponent(email)}`, { headers: { Authorization: `Bearer ${token}` } });
      setBusiness(r.data);
    }
  }, [email, isNew, token]);

  const handleApprove = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/business/${encodeURIComponent(email)}`, { status: "approved", profile_review_needed: false }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Business approved");
      await refresh();
    } catch { toast.error("Failed to approve"); }
    finally { setBusy(false); }
  };

  const handleReject = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/business/${encodeURIComponent(email)}`, { status: "rejected", profile_review_needed: false }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Business rejected");
      await refresh();
    } catch { toast.error("Failed to reject"); }
    finally { setBusy(false); }
  };

  const handleDelistToggle = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/business/${encodeURIComponent(email)}`, { is_delisted: !business.is_delisted }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(business.is_delisted ? "Relisted" : "Delisted");
      await refresh();
    } catch { toast.error("Failed to toggle delist"); }
    finally { setBusy(false); }
  };

  const handleOverrideToggle = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/business/${encodeURIComponent(email)}/toggle-override`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Override toggled");
      await refresh();
    } catch { toast.error("Failed to toggle override"); }
    finally { setBusy(false); }
  };

  const handleMarkReviewed = async () => {
    setBusy(true);
    try {
      const response = await axios.put(`${API}/admin/business/${encodeURIComponent(email)}`, { profile_review_needed: false }, { headers: { Authorization: `Bearer ${token}` } });
      setBusiness(response.data);
      toast.success("Profile edits marked reviewed");
    } catch {
      toast.error("Failed to mark reviewed");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-[#1a1a1a] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-[#1a1a1a]" /></div>
            <div>
              <span className="font-heading text-lg font-semibold">Admin Portal</span>
              <p className="text-xs text-gray-400">614 Shop Local</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Button variant="ghost" onClick={() => navigate("/admin/dashboard")} className="mb-6 text-slate-600 hover:text-slate-900 -ml-2" data-testid="admin-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">{isNew ? "Add New Business" : "Edit Business"}</h1>
            <p className="text-slate-500 mt-1">{isNew ? "Create a new business listing" : business.business_name || business.business_email}</p>
          </div>
          <Button onClick={handleSave} disabled={saving || (!isNew && !hasChanges)} className="h-10 px-6 rounded-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] font-bold disabled:opacity-50" data-testid="admin-save-btn" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              : (isNew || hasChanges) ? <><Save className="w-4 h-4 mr-2" /> Save Changes</>
              : <><CheckCircle className="w-4 h-4 mr-2" /> Saved</>}
          </Button>
        </div>

        <BusinessProfileForm
          data={business}
          onChange={handleChange}
          token={token}
          uploadEndpoint="/api/admin/upload/logo"
          emailReadOnly={false}
          suffixSlot={!isNew ? (
            <AdminStatusCard
              data={business}
              busy={busy}
              approvalKey="status"
              onApprove={handleApprove}
              onReject={handleReject}
              onDelistToggle={handleDelistToggle}
              onOverrideToggle={handleOverrideToggle}
              onMarkReviewed={handleMarkReviewed}
            />
          ) : null}
        />
      </main>
    </div>
  );
};

export default AdminBusinessEditPage;
